import { Badge } from '@/components/ui/Badge'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import type { VenueCampaignDetailView, VenueCampaignResultView } from '@/lib/venue/campaignTypes'
import { CAMPAIGN_STATUS_TONE, readRateText, targetViewSummaryText } from '@/lib/venue/campaignViewModel'
import { EMPTY_MARK, formatAmount, formatDateTime } from '@/lib/venue/format'

/**
 * 상세 — 대상 요약·발송 성공/실패·읽음 지표(요구사항 "상세: 대상 요약, 발송 성공·실패, 읽음/읽음률/
 * 미읽음, 확인 시각 중앙값").
 *
 * 🚨 `charge`는 `NOTICE`면 항상 `null`(#715 §D-2) — 과금 섹션 자체를 렌더하지 않는다(0원 표시 금지).
 * 🚨 `unreadCount`는 응답 필드를 그대로 쓴다(`successCount - readCount`, `recipientCount` 아님 — #728 §5).
 */
export function NoticeCampaignResultSummary({
  detail,
  result,
}: {
  detail: VenueCampaignDetailView
  result: VenueCampaignResultView
}) {
  return (
    <Card>
      <CardHeader
        title={detail.summary.title}
        action={<Badge tone={CAMPAIGN_STATUS_TONE[result.status]}>{result.statusLabel}</Badge>}
      />
      <CardBody className="space-y-3 text-sm text-ink-700">
        <p>대상: {targetViewSummaryText(detail.target)}</p>
        <p>발송일시: {formatDateTime(result.sentAt)}</p>

        {result.statusReason ? <p className="text-danger-500">{result.statusReason}</p> : null}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <p className="text-xs text-slateish-600">발송 성공</p>
            <p className="text-base font-semibold">{result.successCount.toLocaleString('ko-KR')}</p>
          </div>
          <div>
            <p className="text-xs text-slateish-600">발송 실패</p>
            <p className="text-base font-semibold">{result.failedCount.toLocaleString('ko-KR')}</p>
          </div>
          <div>
            <p className="text-xs text-slateish-600">읽음</p>
            <p className="text-base font-semibold">{result.readCount.toLocaleString('ko-KR')}</p>
          </div>
          <div>
            <p className="text-xs text-slateish-600">읽음률</p>
            <p className="text-base font-semibold">{readRateText(result.readRatePercent)}</p>
          </div>
          <div>
            <p className="text-xs text-slateish-600">미읽음</p>
            <p className="text-base font-semibold">{result.unreadCount.toLocaleString('ko-KR')}</p>
          </div>
          <div>
            <p className="text-xs text-slateish-600">확인 시각 중앙값</p>
            <p className="text-base font-semibold">
              {result.medianReadAt ? formatDateTime(result.medianReadAt) : EMPTY_MARK}
            </p>
          </div>
        </div>

        {result.charge ? (
          <div className="rounded-lg bg-slateish-50 px-3 py-2 text-xs text-slateish-600">
            <p>
              과금 건수 {result.charge.chargedCount.toLocaleString('ko-KR')}건 · 확정 금액{' '}
              {formatAmount(result.charge.amount)}({result.charge.statusLabel})
            </p>
            {/* 🚨 estimatedSendCount/estimatedAmount는 항상 함께 null이거나 함께 값이 있다(#731 §8) — 대비 행 자체를 생략한다. */}
            {result.charge.estimatedSendCount !== null && result.charge.estimatedAmount !== null ? (
              <p>
                예상 {result.charge.estimatedSendCount.toLocaleString('ko-KR')}건{' '}
                {formatAmount(result.charge.estimatedAmount)} → 실제 {result.charge.successCount.toLocaleString('ko-KR')}건{' '}
                {formatAmount(result.charge.amount)}
              </p>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
